import { styled } from '@mui/material/styles';
import { Paper, Typography, Box, Tooltip, IconButton } from '@mui/material';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { ServicePlanServiceArea } from '../../../data/models/client-program.model';
import { Client } from '../../../data/models/client.model';
import ServiceAreaListDeprecated from './ServiceAreaListDeprecated';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'ClientServiceAreasDeprecated';

const classes = {
  container: `${PREFIX}-container`,
  details: `${PREFIX}-details`,
  header: `${PREFIX}-header`,
  subheader: `${PREFIX}-subheader`,
  title: `${PREFIX}-title`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    minWidth: 400,
  },

  [`& .${classes.details}`]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },

  [`& .${classes.header}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },

  [`& .${classes.subheader}`]: {
    marginBottom: theme.spacing(1),
    fontWeight: 600,
  },

  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontSize: 21,
    fontWeight: 700,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
}));

interface ClientServiceAreasDeprecatedProps {
  client?: Client;
  serviceAreas: ServicePlanServiceArea[];
  addServiceArea: () => void;
  onActivate: (serviceArea: ServicePlanServiceArea) => void;
  onDeactivate: (serviceArea: ServicePlanServiceArea) => void;
  onEdit: (serviceArea: ServicePlanServiceArea) => void;
  onDelete: (purposeCode: ServicePlanServiceArea) => void;
  onStartDrag: (servicePlan: ServicePlanServiceArea) => void;
  onEndDrag: (servicePlan: ServicePlanServiceArea) => void;
}

export default function ClientServiceAreasDeprecated({
  client,
  serviceAreas,
  addServiceArea,
  onActivate,
  onDeactivate,
  onEdit,
  onDelete,
  onStartDrag,
  onEndDrag,
}: ClientServiceAreasDeprecatedProps) {
  const t = useCommonTranslation();

  const isSuperReaderUser = useIsSuperReaderUser();

  return (
    <StyledBox className={classes.container}>
      <Paper elevation={2} className={classes.details}>
        <Box className={classes.header}>
          <Typography className={classes.title}>
            {t('client.serviceAreas')}
          </Typography>
          {!isSuperReaderUser && (
            <Tooltip title={t('client.addServiceArea')}>
              <span>
                <IconButton
                  data-testid="add-service-area"
                  onClick={addServiceArea}
                  color="primary"
                  size="large">
                  <AddCircleOutlineOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
        <Box className={classes.subheader}>{t('client.dragServiceArea')}</Box>
        {client && (
          <ServiceAreaListDeprecated
            client={client}
            serviceAreas={serviceAreas}
            onActivate={onActivate}
            onDeactivate={onDeactivate}
            onEdit={onEdit}
            onDelete={onDelete}
            onStartDrag={onStartDrag}
            onEndDrag={onEndDrag}
          />
        )}
      </Paper>
    </StyledBox>
  );
}
